import { Call02Icon, ChipIcon, Clock01Icon, CpuIcon, EnergyIcon, FlashIcon, HardDriveIcon, Home01Icon, Location03Icon, PackageIcon, Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { address, email, phone, workingTime } from 'src/constants';
import { getTel } from 'src/helpers';
import { useCategories, useProducts } from 'src/hooks';

const ICON_POOL = [CpuIcon, ChipIcon, EnergyIcon, FlashIcon, HardDriveIcon, PackageIcon];

const pickIcon = (name: string, fallback: any) => {
	const k = (name || '').toLowerCase();
	if (k.includes('plc') || k.includes('hmi') || k.includes('cpu')) return CpuIcon;
	if (k.includes('chip') || k.includes('cảm biến') || k.includes('sensor')) return ChipIcon;
	if (k.includes('biến tần') || k.includes('servo') || k.includes('điện')) return EnergyIcon;
	if (k.includes('đo') || k.includes('meter')) return FlashIcon;
	if (k.includes('lưu trữ') || k.includes('drive')) return HardDriveIcon;
	return fallback;
};

const shortAddress = (addr: string) => {
	const parts = addr.split(',').map((p) => p.trim());
	return parts.slice(-2).join(', ');
};

const Header = () => {
	const [search, setSearch] = useState('');
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	const { categories } = useCategories();
	const { products } = useProducts({ categories });

	useEffect(() => {
		if (location.pathname.startsWith('/tim-kiem')) {
			setSearch(searchParams.get('q') ?? '');
		} else {
			setSearch('');
		}
	}, [location.pathname, searchParams]);

	const onSearch = (e?: React.FormEvent) => {
		e?.preventDefault();
		const q = search.trim();
		navigate(q ? `/tim-kiem?q=${encodeURIComponent(q)}` : '/tim-kiem');
	};

	const tabs = useMemo(() => {
		const counts: Record<string, number> = {};
		products.forEach((p) => {
			counts[p.categoryId] = (counts[p.categoryId] ?? 0) + 1;
		});
		return [
			{ id: '', name: 'Tất cả', icon: Home01Icon, count: 0 },
			...categories.map((c, i) => ({
				id: c.id,
				name: c.name,
				count: counts[c.id] ?? 0,
				icon: pickIcon(c.name, ICON_POOL[i % ICON_POOL.length]),
			})),
		];
	}, [categories, products]);

	const activeCat = searchParams.get('cat');
	const onSearchRoute = location.pathname.startsWith('/tim-kiem');
	const isHome = location.pathname === '/';

	return (
		<header className='bg-white sticky top-0 z-50 shadow-[0_1px_0_#e5e7eb]'>
			<div className='border-b border-gray-100 text-xs text-gray-500'>
				<div className='container'>
					<div className='flex justify-between items-center h-[30px] gap-3'>
						<div className='hidden md:flex items-center gap-3.5'>
							<span className='inline-flex items-center gap-1'>
								<HugeiconsIcon icon={Location03Icon} size={11} />
								{shortAddress(address)}
							</span>
							<span className='inline-flex items-center gap-1'>
								<HugeiconsIcon icon={Clock01Icon} size={11} />
								{workingTime}
							</span>
						</div>
						<div className='flex items-center gap-3.5 ml-auto'>
							<Link to='/chinh-sach-bao-hanh' className='hover:text-[#1c66a8]'>
								Hỗ trợ
							</Link>
							<Link to='/gioi-thieu' className='hidden md:inline hover:text-[#1c66a8]'>
								Về D&L
							</Link>
							<Link to='/tin-tuc' className='hidden md:inline hover:text-[#1c66a8]'>
								Tin tức
							</Link>
							<a href={`mailto:${email}`} className='hidden md:inline hover:text-[#1c66a8]'>
								{email}
							</a>
						</div>
					</div>
				</div>
			</div>

			<div className='container'>
				<div className='grid items-center gap-7 py-[18px]' style={{ gridTemplateColumns: 'auto 1fr auto' }}>
					<Link to='/' className='flex items-center gap-3'>
						<img src='/logo2.png' alt='D&L' className='h-12' />
						<div className='hidden md:block pl-3.5 border-l-2 border-gray-200'>
							<div className='text-[11px] text-gray-400 tracking-[0.1em] font-semibold uppercase'>Điện tử D&L</div>
							<div className='text-[13px] text-[#1c66a8] font-semibold mt-px'>Công nghiệp · Hàng hải</div>
						</div>
					</Link>

					<form onSubmit={onSearch} className='flex items-stretch border-2 border-[#1c66a8] rounded-md max-w-[640px] w-full mx-auto'>
						<div className='flex items-center px-3 text-[#1c66a8]'>
							<HugeiconsIcon icon={Search01Icon} size={18} />
						</div>
						<input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder='Bạn đang tìm thiết bị gì hôm nay?'
							aria-label='Tìm kiếm'
							className='flex-1 min-w-0 border-none outline-none py-2.5 text-sm bg-transparent'
						/>
						<button type='submit' className='px-6 bg-[#1c66a8] hover:bg-[#195b96] text-white font-bold text-[13px] transition'>
							Tìm
						</button>
					</form>

					<a href={`tel:${getTel()}`} className='hidden md:flex items-center gap-2.5'>
						<div className='relative w-[42px] h-[42px] rounded-full bg-red-50 text-red-500 flex items-center justify-center'>
							<HugeiconsIcon icon={Call02Icon} size={18} />
							<span className='absolute rounded-full border-2 border-red-500 opacity-60' style={{ inset: -2, animation: 'ping 2s infinite' }} />
						</div>
						<div>
							<div className='text-[11px] text-gray-400 tracking-[0.08em] font-semibold uppercase'>Tư vấn ngay</div>
							<div className='text-base text-red-500 font-bold leading-tight'>{phone}</div>
						</div>
					</a>
				</div>

				<div className='flex items-center border-t border-gray-100 overflow-x-auto -mb-px custom-scrollbar'>
					{tabs.map((t) => {
						const active = t.id ? activeCat === t.id : isHome || (onSearchRoute && !activeCat);
						return (
							<Link
								key={t.id || 'all'}
								to={t.id ? `/tim-kiem?cat=${t.id}` : '/'}
								className='flex items-center gap-2 px-4 py-3.5 text-[13.5px] font-semibold whitespace-nowrap transition-colors'
								style={{
									color: active ? '#1c66a8' : '#4b5563',
									borderBottom: `3px solid ${active ? '#1c66a8' : 'transparent'}`,
								}}
							>
								<HugeiconsIcon icon={t.icon} size={15} />
								{t.name}
							</Link>
						);
					})}
				</div>
			</div>
		</header>
	);
};

export default Header;
